import { useState } from 'react';
import questionIcon from '../../assets/icons/question.svg';
import Button from '../shared/Button';
import { X } from 'lucide-react';

const styles = {
	section: 'fixed inset-0 z-[9999] flex items-center justify-center p-4',
	backDrop: 'absolute inset-0 bg-black/70',
	modal: 'relative z-10 w-full max-w-[500px] rounded-[18px] bg-white shadow-2xl p-[30px]',
	heading: 'text-25 font-bold text-dark-blue xl:mb-3 md:mb-1 leading-100',
	subHeading: 'text-gray text-sm xl:mb-6 md:mb-2',
	label: 'text-base leading-100 font-medium text-dark-blue',
	note: 'bg-lightPurple p-4 rounded-xl text-sm leading-21 text-dark-blue font-medium italic xl:mb-8 mb-3 flex flex-col',
	span: 'font-semibold text-base leading-100 text-purple xl:mb-2.5 mb-1'
};

export default function CreateDocuementModal({ isOpen, onClose, isImported, onDocumentCreated }) {
	// Updated props
	const [documentTitle, setDocumentTitle] = useState('');
	const [formattingStyle, setFormattingStyle] = useState('');
	const [documentType, setDocumentType] = useState('');
	const [university, setUniversity] = useState('');

	// Mock data for dropdowns
	const formattingStyles = ['APA 7th Edition', 'MLA 9th Edition', 'Chicago Style'];
	const documentTypes = ['Student Paper', 'Research Proposal', 'Thesis', 'Dissertation'];
	const universities = ['University of Texas at Austin', 'Harvard University', 'Yale University'];

	if (!isOpen) return null;

	const handleBackdropClick = (e) => {
		if (e.target === e.currentTarget) onClose();
	};

	const handleCreateDocument = () => {
		// In a real app, you would send this data to an API
		// and receive a documentId back. For now, we'll mock one.
		const newDocumentId = Math.floor(Math.random() * 1000) + 100; // Mock ID
		onClose(); // Close the modal
		onDocumentCreated(newDocumentId); // Call the prop with the new document ID
	};

	// Reusable styles for custom dropdown arrow (SVG) and padding-right: 5px
	const customSelectClasses =
		'w-full rounded-[10px] border-1p3 border-[#E8E8EA] px-4 xl:py-3 py-2 text-base appearance-none bg-no-repeat focus:outline-none focus:ring-0 focus:border-[#E8E8EA]';
	const customSelectStyle = {
		backgroundImage:
			"url(\"data:image/svg+xml;utf8,<svg width='23' height='23' viewBox='0 0 23 23' fill='none' xmlns='http://www.w3.org/2000/svg'><g opacity='0.5'><path d='M16.2209 8.55273L11.7879 15.1527L7.4209 8.55273H16.2209Z' fill='%23180A63'/></g></svg>\")",
		backgroundSize: '23px 23px',
		// position the icon at the right with 5px inset and vertically centered
		backgroundPosition: 'right 5px center',
		// requested padding-right of 5px so text doesn't sit under the icon
		paddingRight: '20px'
	};

	return (
		<div className={styles.section} onClick={handleBackdropClick}>
			{/* Backdrop */}
			<div className={styles.backDrop} />

			{/* Modal */}
			<div className={styles.modal}>
				{isImported && (
					<div className="w-14 h-7 p-2 rounded-[8px] bg-[#F2F3F7] mb-3">
						<p className="text-xs font-medium text-dark-blue">Step 2</p>
					</div>
				)}
				<button
					onClick={onClose}
					className="absolute top-8 right-4 md:right-6 w-5 h-5 bg-dark-blue/40 hover:bg-dark-blue/80 rounded-full flex items-center justify-center transition-colors"
				>
					<X className="w-3 h-3 text-white" />
				</button>

				{/* Heading */}
				<h2 className={styles.heading}>Create New Document</h2>
				<p className={styles.subHeading}>Enter the details below to create a new document</p>

				<div className="h-[1px] bg-[#F0F0F0] xl:mb-[25px] mb-2 mx-[-30px]" />

				{/* Input fields */}
				<div className="grid grid-cols-1 gap-6 xl:mb-4 mb-2">
					{/* Document Title */}
					<div>
						<div className="flex items-center gap-2 xl:mb-3 mb-1">
							<label htmlFor="document-title" className={styles.label}>
								Document title
							</label>
							<img src={questionIcon} alt="help icon" className="w-3 h-3" />
						</div>
						<input
							id="document-title"
							type="text"
							placeholder="Introduction to Climate Change"
							value={documentTitle}
							onChange={(e) => setDocumentTitle(e.target.value)}
							className="w-full rounded-[10px] border-1p3 border-lightGrey px-4 xl:py-3 py-2 text-base"
						/>
					</div>

					{/* Formatting Style */}
					<div>
						<div className="flex items-center gap-2 xl:mb-3 mb-1">
							<label htmlFor="formatting-style" className={styles.label}>
								Formatting style
							</label>
							<img src={questionIcon} alt="help icon" className="w-3 h-3" />
						</div>
						<select
							id="formatting-style"
							value={formattingStyle}
							onChange={(e) => setFormattingStyle(e.target.value)}
							className={customSelectClasses}
							style={customSelectStyle}
						>
							<option value="" disabled hidden>
								Select style
							</option>
							{formattingStyles.map((style) => (
								<option key={style} value={style}>
									{style}
								</option>
							))}
						</select>
					</div>

					{/* Document Type */}
					<div>
						<div className="flex items-center gap-2 xl:mb-3 mb-1">
							<label htmlFor="document-type" className={styles.label}>
								Document type
							</label>
							<img src={questionIcon} alt="help icon" className="w-3 h-3" />
						</div>
						<select
							id="document-type"
							value={documentType}
							onChange={(e) => setDocumentType(e.target.value)}
							className={customSelectClasses}
							style={customSelectStyle}
						>
							<option value="" disabled hidden>
								Select type
							</option>
							{documentTypes.map((type) => (
								<option key={type} value={type}>
									{type}
								</option>
							))}
						</select>
					</div>

					{/* University */}
					<div>
						<div className="flex items-center gap-2 xl:mb-3 mb-1">
							<label htmlFor="university" className={styles.label}>
								University
							</label>
							<img src={questionIcon} alt="help icon" className="w-3 h-3" />
						</div>
						<select
							id="university"
							value={university}
							onChange={(e) => setUniversity(e.target.value)}
							className={customSelectClasses}
							style={customSelectStyle}
						>
							<option value="" disabled hidden>
								Select university
							</option>
							{universities.map((uni) => (
								<option key={uni} value={uni}>
									{uni}
								</option>
							))}
						</select>
					</div>
				</div>

				{/* Important note */}
				<div className={styles.note}>
					<span className={styles.span}>Important note:</span> Formatting follows public style
					guidelines. Not affiliated with any official organization.
				</div>

				<div className="h-[1px] bg-[#F0F0F0] xl:mb-[25px] mb-2 mx-[-30px]" />

				{/* Buttons */}
				<div className="flex xl:gap-3 gap-2">
					<Button onClick={handleCreateDocument}>Create Document</Button>
				</div>
			</div>
		</div>
	);
}
